/**
 * SimpleAvatar Component
 *
 * Reusable avatar component displaying initials in a colored circle.
 * Used for displaying user/attendee avatars throughout the app.
 *
 * Follows single responsibility principle: only renders avatar UI.
 */

import { Image, StyleSheet, Text, View } from 'react-native';
import { ModernPalette } from '@/constants/theme';

type SimpleAvatarProps = {
  name: string;
  color: string;
  size: number;
  textColor?: string;
  imageUri?: string | null;
};

/**
 * Extracts initials from a name string.
 * Takes first letter of first two words.
 */
function getInitials(name: string): string {
  if (!name || typeof name !== 'string') {
    return '?';
  }

  return name
    .split(' ')
    .filter(word => word.length > 0)
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';
}

/**
 * SimpleAvatar Component
 *
 * Displays a circular avatar with profile picture or initials fallback.
 */
export function SimpleAvatar({
  name,
  color,
  size,
  textColor = ModernPalette.textInverse,
  imageUri,
}: SimpleAvatarProps) {
  const displayName = name || 'Unknown';
  const initials = getInitials(displayName);
  const fontSize = size * 0.4;

  // Show profile picture if available
  if (imageUri) {
    return (
      <Image
        source={{ uri: imageUri }}
        style={[
          styles.image,
          {
            width: size,
            height: size,
            borderRadius: size / 2,
          },
        ]}
      />
    );
  }

  // Fall back to initials
  return (
    <View
      style={[
        styles.container,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: color,
        },
      ]}
    >
      <Text style={[styles.initials, { fontSize, color: textColor }]}>
        {initials}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  initials: {
    fontWeight: '600',
  },
  image: {
    resizeMode: 'cover',
  },
});
